import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import { ConsentLog, getConsentGroupKey } from "@/utils/consentLogGrouping";

interface UseConsentLogsParams {
  page: number;
  pageSize: number;
  searchTerm?: string;
  statusFilter?: "all" | "approved" | "withdrawn";
  consentTypeFilter?: string;
}

interface ConsentLogsStats {
  totalApproved: number;
  totalWithdrawn: number;
}

interface UseConsentLogsResult {
  data: ConsentLog[] | undefined;
  pagination: ContentPagination | undefined;
  stats: ConsentLogsStats | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

function buildConsentLogsQuery({
  searchTerm,
  statusFilter,
  consentTypeFilter,
}: {
  searchTerm?: string;
  statusFilter?: "all" | "approved" | "withdrawn";
  consentTypeFilter?: string;
}) {
  let query = supabase
    .from("consent_logs")
    .select("*")
    .order("timestamp", { ascending: false });

  if (statusFilter === "approved") {
    query = query.eq("granted", true);
  } else if (statusFilter === "withdrawn") {
    query = query.eq("granted", false);
  }

  if (consentTypeFilter && consentTypeFilter !== "all") {
    query = query.eq("consent_type", consentTypeFilter);
  }

  if (searchTerm?.trim()) {
    const searchPattern = searchTerm.trim();
    query = query.or(
      `email.ilike.%${searchPattern}%,ip_address.ilike.%${searchPattern}%,id.ilike.%${searchPattern}%,browser_name.ilike.%${searchPattern}%,os_name.ilike.%${searchPattern}%,device_type.ilike.%${searchPattern}%,device_brand.ilike.%${searchPattern}%,device_model.ilike.%${searchPattern}%`
    );
  }

  return query;
}

export function useConsentLogs({
  page,
  pageSize,
  searchTerm = "",
  statusFilter = "all",
  consentTypeFilter,
}: UseConsentLogsParams): UseConsentLogsResult {
  const {
    data: allLogs,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "consent-logs",
      searchTerm,
      statusFilter,
      consentTypeFilter,
    ],
    queryFn: async () => {
      const { data, error: dataError } = await buildConsentLogsQuery({
        searchTerm,
        statusFilter,
        consentTypeFilter,
      });

      if (dataError) throw new Error(dataError.message);

      return (data as ConsentLog[]) || [];
    },
  });

  const { data, pagination, stats } = useMemo(() => {
    const logs = allLogs || [];

    const orderedGroupKeys: string[] = [];
    const seen = new Set<string>();
    logs.forEach((log) => {
      const key = getConsentGroupKey(log);
      if (!seen.has(key)) {
        seen.add(key);
        orderedGroupKeys.push(key);
      }
    });

    const totalGroups = orderedGroupKeys.length;
    const totalPages = Math.max(1, Math.ceil(totalGroups / pageSize));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const pageGroupKeys = orderedGroupKeys.slice(
      startIndex,
      startIndex + pageSize
    );
    const pageGroupKeySet = new Set(pageGroupKeys);
    const pageLogs = logs.filter((log) =>
      pageGroupKeySet.has(getConsentGroupKey(log))
    );

    const totalApproved = logs.filter((log) => log.granted).length;
    const totalWithdrawn = logs.length - totalApproved;

    const paginationResult: ContentPagination = {
      currentPage: safePage,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPrevPage: safePage > 1,
      totalCount: totalGroups,
    };

    return {
      data: pageLogs,
      pagination: paginationResult,
      stats: {
        totalApproved,
        totalWithdrawn,
      },
    };
  }, [allLogs, page, pageSize]);

  return {
    data,
    pagination,
    stats,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Fetches all consent logs matching the provided filters (no pagination)
 * If no filters are applied, returns all consent logs
 */
export async function fetchAllFilteredConsentLogs({
  searchTerm = "",
  statusFilter = "all",
  consentTypeFilter,
}: {
  searchTerm?: string;
  statusFilter?: "all" | "approved" | "withdrawn";
  consentTypeFilter?: string;
}): Promise<ConsentLog[]> {
  const { data, error } = await buildConsentLogsQuery({
    searchTerm,
    statusFilter,
    consentTypeFilter,
  });

  if (error) {
    throw new Error(error.message || "Failed to fetch consent logs");
  }

  return (data as ConsentLog[]) || [];
}
